import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GUI } from 'three/addons/libs/lil-gui.module.min.js';

// ==========================================
// 1. ESTADO DE LA APLICACIÓN
// ==========================================

const state = {

    // Dimensiones
    totalWidth: 600,
    depth: 450,
    thickness: 15,       
    zocloHeight: 100,    
    recess: 80,         
    tenon: 5,            
    bitDiameter: 10,     
    
    // Apariencia
    color: '#c4bc00',
    
    shelfCount: 5,       
    gaps: [],            
    calculatedHeight: 0, 

    // Producción
    sheetWidth: 1220,
    sheetHeight: 2440,
    sheetMargin: 40,
    partGap: 25, 
    dxfSpacing: 300,

    currentSheetIndex: 0,
    totalSheets: 1,
    
    exportDXF: function() { exportToDXF(); }
};

let partsData = []; 

// ==========================================
// 2. SETUP THREE.JS
// ==========================================

const scene = new THREE.Scene();
scene.background = new THREE.Color(0xe0e0e0);

const container3D = document.getElementById('view-3d');
const camera = new THREE.PerspectiveCamera(45, container3D.clientWidth / container3D.clientHeight, 1, 10000);
camera.position.set(2500, 2000, 2500);

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
renderer.setSize(container3D.clientWidth, container3D.clientHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Sombras suaves
container3D.appendChild(renderer.domElement);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.1;

const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.7);
scene.add(hemiLight);
const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
dirLight.position.set(1000, 3000, 1000);
dirLight.castShadow = true;
// Mejorar calidad de sombra
dirLight.shadow.mapSize.width = 2048;
dirLight.shadow.mapSize.height = 2048;
const d = 2000;
dirLight.shadow.camera.left = -d;
dirLight.shadow.camera.right = d;
dirLight.shadow.camera.top = d;
dirLight.shadow.camera.bottom = -d;
dirLight.shadow.camera.far = 10000;
dirLight.shadow.bias = -0.0005;
scene.add(dirLight);

// --- PISO DE SOMBRAS ---
const planeGeo = new THREE.PlaneGeometry(5000, 5000);
const planeMat = new THREE.ShadowMaterial({ opacity: 0.15 }); 
const plane = new THREE.Mesh(planeGeo, planeMat);
plane.rotation.x = -Math.PI / 2;
plane.position.y = 0; 
plane.receiveShadow = true;
scene.add(plane);

const structureGroup = new THREE.Group(); 
scene.add(structureGroup);

// Materiales Globales

const materials = {
    board: new THREE.MeshStandardMaterial({ color: state.color, roughness: 0.4 }),
    edge: new THREE.LineBasicMaterial({ color: 0xffffff, opacity: 0.3, transparent: true })
};

// ==========================================
// 3. GENERACIÓN DEL SISTEMA
// ==========================================

function generateSystem() {
    while(structureGroup.children.length > 0) {
        const obj = structureGroup.children[0];
        structureGroup.remove(obj);
        if(obj.geometry) obj.geometry.dispose();
    }
    partsData = []; 

    materials.board.color.set(state.color);

    const { totalWidth, depth, thickness, gaps, zocloHeight, recess, tenon } = state;
    
    // --- CÁLCULOS MATEMÁTICOS PRECISOS ---
    const innerW = totalWidth - (thickness * 2); 
    const tenonedW = innerW + (tenon * 2); 

    const frontZ = depth / 2;
    const backZ = -depth / 2;
    const backPanelFrontFaceZ = backZ + thickness;
    const backPanelCenterZ = backZ + thickness / 2;

    const shelfFrontZ = frontZ - recess;
    const shelfBackZ = backPanelFrontFaceZ - tenon; 
    const shelfPhysicalDepth = shelfFrontZ - shelfBackZ;
    const shelfCenterZ = (shelfFrontZ + shelfBackZ) / 2;

    const sidePockets = []; 
    const backPockets = []; 

    let currentY = zocloHeight;

    // A. PISO
    addPart('Piso', tenonedW, thickness, shelfPhysicalDepth, 0, currentY + thickness/2, shelfCenterZ);
    sidePockets.push({ y: currentY, h: thickness, d: shelfPhysicalDepth, type: 'shelf' });
    backPockets.push({ y: currentY, h: thickness, w: tenonedW, type: 'shelf' }); 
    currentY += thickness;

    // B. ENTREPAÑOS
    gaps.forEach((gapSize, index) => {
        currentY += gapSize;
        if (index < gaps.length - 1) {
            addPart(`Entrepaño_${index+1}`, tenonedW, thickness, shelfPhysicalDepth, 0, currentY + thickness/2, shelfCenterZ);
            sidePockets.push({ y: currentY, h: thickness, d: shelfPhysicalDepth, type: 'shelf' });
            backPockets.push({ y: currentY, h: thickness, w: tenonedW, type: 'shelf' });
            currentY += thickness;
        }
    });

    // C. TECHO
    addPart('Techo', tenonedW, thickness, shelfPhysicalDepth, 0, currentY + thickness/2, shelfCenterZ);
    sidePockets.push({ y: currentY, h: thickness, d: shelfPhysicalDepth, type: 'shelf' });
    backPockets.push({ y: currentY, h: thickness, w: tenonedW, type: 'shelf' });
    currentY += thickness;

    state.calculatedHeight = currentY;
    
    // D. TAPA TRASERA
    addPart('Tapa_Trasera', tenonedW, currentY, thickness, 0, currentY/2, backPanelCenterZ, backPockets);

    // E. LATERALES
    sidePockets.push({
        y: 0,                 
        h: currentY,          
        d: thickness,         
        type: 'groove'        
    });

    addPart('Lateral_Izq', thickness, currentY, depth, -totalWidth/2 + thickness/2, currentY/2, 0, sidePockets);
    addPart('Lateral_Der', thickness, currentY, depth, totalWidth/2 - thickness/2, currentY/2, 0, sidePockets);

    controls.target.y = currentY / 2;
    calculateNesting();
}

/**
 * addPart
 */

function addPart(name, w, h, d, x, y, z, pockets = []) {

    // 1. GEOMETRÍA 3D
    const geo = new THREE.BoxGeometry(w, h, d);
    const mesh = new THREE.Mesh(geo, materials.board);
    mesh.position.set(x, y, z);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(geo), materials.edge));
    structureGroup.add(mesh);

    // 2. DATOS PARA NESTING 2D
    const dims = [w, h, d].sort((a,b) => a-b);
    const dimMid = dims[1]; 
    const dimMax = dims[2];

    let cutW, cutH;
    let processedPockets = []; 

    if (name.includes('Lateral')) {
        cutW = dimMid; cutH = dimMax; 
        const isRight = name.includes('Der');

        pockets.forEach(p => {
            if (p.type === 'shelf') {
                const pocketX = isRight ? (cutW - p.d) : 0;
                processedPockets.push({ x: pocketX, y: p.y, w: p.d, h: p.h, type: 'shelf' });
            } 
            else if (p.type === 'groove') {
                const grooveX = isRight ? (cutW - p.d) : 0;
                processedPockets.push({ x: grooveX, y: p.y, w: p.d, h: p.h, type: 'groove' });
            }
        });

    } else if (name.includes('Tapa_Trasera')) {
        cutW = dimMid; cutH = dimMax; 
        
        pockets.forEach(p => {
            const marginX = (cutW - p.w) / 2;
            processedPockets.push({ x: marginX, y: p.y, w: p.w, h: p.h, type: 'shelf' });
        });

    } else {
        cutW = dimMax; cutH = dimMid; 
    }

    partsData.push({
        name: name, w: cutW, h: cutH,
        x: 0, y: 0, sheetIndex: -1, fits: true,
        pockets: processedPockets 
    });
}

// ==========================================
// 4. NESTING (PACKER)
// ==========================================

class Packer {
    constructor(w, h) { this.root = { x: 0, y: 0, w: w, h: h }; }
    fit(blocks) {
        let n, node, block;
        for (n = 0; n < blocks.length; n++) {
            block = blocks[n];
            if (node = this.findNode(this.root, block.w, block.h))
                block.fit = this.splitNode(node, block.w, block.h);
        }
    }
    findNode(root, w, h) {
        if (root.used) return this.findNode(root.right, w, h) || this.findNode(root.down, w, h);
        else if ((w <= root.w) && (h <= root.h)) return root;
        else return null;
    }
    splitNode(node, w, h) {
        node.used = true;
        node.down = { x: node.x, y: node.y + h, w: node.w, h: node.h - h };
        node.right = { x: node.x + w, y: node.y, w: node.w - w, h: h };
        return node;
    }
}

function calculateNesting() {
    const margin = state.sheetMargin;
    const gap = state.partGap;
    const sheetW = state.sheetWidth;
    const sheetH = state.sheetHeight;
    const availW = sheetW - (margin * 2);
    const availH = sheetH - (margin * 2);

    partsData.forEach(p => p.fits = false);
    partsData.sort((a, b) => b.h - a.h);

    let currentSheet = 0;
    let partsToPack = [...partsData];
    
    while (partsToPack.length > 0) {
        const packer = new Packer(availW, availH);
        let blocks = [];
        for (let i = 0; i < partsToPack.length; i++) {
            const p = partsToPack[i];
            if (p.w > availW || p.h > availH) {
                if (p.h <= availW && p.w <= availH) {
                    const t = p.w; p.w = p.h; p.h = t;
                } else {
                    p.fits = false; p.sheetIndex = -1;
                    partsToPack.splice(i, 1); i--; continue;
                }
            }
            blocks.push({ w: p.w + gap, h: p.h + gap, data: p });
        }
        packer.fit(blocks);
        let unpacked = [];
        for (let j = 0; j < blocks.length; j++) {
            const block = blocks[j];
            const p = block.data;
            if (block.fit) {
                p.fits = true; p.sheetIndex = currentSheet;
                p.x = margin + block.fit.x; p.y = margin + block.fit.y;
            } else {
                unpacked.push(p);
            }
        }
        partsToPack = unpacked;
        if (partsToPack.length > 0) { currentSheet++; if (currentSheet > 50) break; }
    }

    state.totalSheets = currentSheet + 1;
    if (state.currentSheetIndex >= state.totalSheets) state.currentSheetIndex = Math.max(0, state.totalSheets - 1);

    updateUI();
    draw2D();
}

// ==========================================
// 5. VISUALIZACIÓN UI
// ==========================================

const canvas2D = document.getElementById('canvas2d');
const ctx2D = canvas2D.getContext('2d');
const btnPrev = document.getElementById('btn-prev');
const btnNext = document.getElementById('btn-next');
const indicator = document.getElementById('page-indicator');
const statsSheet = document.getElementById('sheet-stats');
const statsError = document.getElementById('error-stats');

btnPrev.addEventListener('click', () => { if(state.currentSheetIndex > 0) { state.currentSheetIndex--; draw2D(); updateUI(); } });
btnNext.addEventListener('click', () => { if(state.currentSheetIndex < state.totalSheets - 1) { state.currentSheetIndex++; draw2D(); updateUI(); } });

function updateUI() {
    indicator.innerText = `Hoja ${state.currentSheetIndex + 1} de ${state.totalSheets}`;
    btnPrev.disabled = state.currentSheetIndex === 0;
    btnNext.disabled = state.currentSheetIndex === state.totalSheets - 1;
    const validParts = partsData.filter(p => p.fits).length;
    const errorParts = partsData.filter(p => !p.fits).length;
    statsSheet.innerText = `Piezas Válidas: ${validParts}`;
    statsError.innerText = errorParts > 0 ? `⚠️ ${errorParts} PIEZA(S) NO CABEN` : '';
}

function draw2D() {
    const w = canvas2D.parentNode.clientWidth;
    const h = canvas2D.parentNode.clientHeight;
    canvas2D.width = w; canvas2D.height = h;

    ctx2D.fillStyle = "#1a1a1a"; ctx2D.fillRect(0,0,w,h);
    const visualWidthTotal = state.sheetWidth + 500; 
    const marginVis = 40;
    const scale = Math.min((w - marginVis*2) / visualWidthTotal, (h - marginVis*2) / state.sheetHeight);
    const drawSheetW = state.sheetWidth * scale;
    const drawSheetH = state.sheetHeight * scale;
    const originX = 50;
    const originY = (h - drawSheetH) / 2;

    ctx2D.fillStyle = "#2d2d2d"; ctx2D.fillRect(originX, originY, drawSheetW, drawSheetH);
    ctx2D.strokeStyle = "#00ffff"; ctx2D.lineWidth = 2; ctx2D.strokeRect(originX, originY, drawSheetW, drawSheetH);
    const m = state.sheetMargin * scale;
    ctx2D.strokeStyle = "#444"; ctx2D.lineWidth = 1; ctx2D.setLineDash([5, 5]);
    ctx2D.strokeRect(originX + m, originY + m, drawSheetW - m*2, drawSheetH - m*2);
    ctx2D.setLineDash([]);

    const sheetParts = partsData.filter(p => p.sheetIndex === state.currentSheetIndex && p.fits);
    sheetParts.forEach(p => {
        const px = originX + (p.x * scale);
        const py = originY + (p.y * scale);
        const pw = p.w * scale;
        const ph = p.h * scale;
        
        ctx2D.fillStyle = "rgba(40, 167, 69, 0.5)"; ctx2D.fillRect(px, py, pw, ph);
        ctx2D.strokeStyle = "#fff"; ctx2D.setLineDash([]); ctx2D.strokeRect(px, py, pw, ph);
        
        // DIBUJAR CAJAS (Pockets)
        if (p.pockets && p.pockets.length > 0) {
            ctx2D.fillStyle = "rgba(0, 0, 255, 0.3)";
            ctx2D.strokeStyle = "#0000ff"; 
            
            p.pockets.forEach(pocket => {
                let pX = pocket.x;
                let pY = pocket.y;
                let pW = pocket.w;
                let pH = pocket.h;
                const radius = state.bitDiameter / 2;

                if (pocket.type === 'shelf') {
                    pX -= radius; pW += state.bitDiameter;
                } else if (pocket.type === 'groove') {
                    pY -= radius; pH += state.bitDiameter;
                }

                const drawX = px + (pX * scale);
                const relativeY = p.h - pY - pH; 
                const drawY = py + (relativeY * scale);
                const drawW = pW * scale;
                const drawH = pH * scale;
                const drawR = radius * scale;

                ctx2D.beginPath();
                ctx2D.roundRect(drawX, drawY, drawW, drawH, drawR);
                ctx2D.fill();
                ctx2D.stroke();
            });
        }

        if (pw > 20) {
            ctx2D.fillStyle = "#fff"; ctx2D.font = "10px sans-serif";
            if(ph > pw && ph > 30) {
                ctx2D.save(); ctx2D.translate(px + pw/2, py + ph/2); ctx2D.rotate(-Math.PI/2);
                ctx2D.fillText(`${Math.round(p.w)}x${Math.round(p.h)}`, -15, 0); ctx2D.restore();
            } else {
                ctx2D.fillText(`${Math.round(p.w)}x${Math.round(p.h)}`, px+2, py+12);
            }
        }
    });

    const errorParts = partsData.filter(p => !p.fits);
    if (errorParts.length > 0) {
        const errorOriginX = originX + drawSheetW + 20;
        ctx2D.fillStyle = "#ff4444"; ctx2D.font = "bold 12px sans-serif";
        ctx2D.fillText("NO CABEN:", errorOriginX, originY - 10);
        errorParts.forEach(p => {
            const px = errorOriginX; 
            const errorIdx = partsData.indexOf(p);
            const py = originY + (errorIdx * 25); 
            const pw = p.w * scale; const ph = p.h * scale;
            ctx2D.fillStyle = "rgba(255, 0, 0, 0.3)"; ctx2D.fillRect(px, py, pw, ph);
            ctx2D.strokeStyle = "#ff0000"; ctx2D.strokeRect(px, py, pw, ph);
            ctx2D.fillStyle = "#ffcccc";
            ctx2D.fillText(p.name, px, py - 3);
            ctx2D.fillText(`${Math.round(p.w)}x${Math.round(p.h)}`, px+2, py+12);
        });
    }
}

// ==========================================
// 6. EXPORTACIÓN DXF
// ==========================================

function exportToDXF() {
    if (partsData.length === 0) return;

    const COL_HOJA = 11776947; 
    const COL_CORTE = 16711680; 
    const COL_CAJA = 255;       

    let s = "0\nSECTION\n2\nHEADER\n0\nENDSEC\n0\nSECTION\n2\nENTITIES\n";

    for (let i = 0; i < state.totalSheets; i++) {
        const offsetGlobalX = i * (state.sheetWidth + state.dxfSpacing);
        s += dxfRect(offsetGlobalX, 0, state.sheetWidth, state.sheetHeight, "Hojas", COL_HOJA);
        
        const parts = partsData.filter(p => p.sheetIndex === i && p.fits);
        
        parts.forEach(p => {
            const absX = offsetGlobalX + p.x; 
            const absY = p.y;
            s += dxfRect(absX, absY, p.w, p.h, "Corte", COL_CORTE);

            if (p.pockets && p.pockets.length > 0) {
                p.pockets.forEach(k => {
                    const pocketAbsY = absY + k.y;
                    const radius = state.bitDiameter / 2;
                    let realX = absX + k.x;
                    let realY = pocketAbsY;
                    let realW = k.w;
                    let realH = k.h;

                    if (k.type === 'shelf') {
                        realX -= radius; realW += state.bitDiameter;
                    } else if (k.type === 'groove') {
                        realY -= radius; realH += state.bitDiameter;
                    }

                    s += dxfRoundedRect(realX, realY, realW, realH, radius, "Cajas", COL_CAJA);
                });
            }
        });
    }

    s += "0\nENDSEC\n0\nEOF";
    downloadFile(s, `Corte_2D_${Math.round(state.calculatedHeight)}.dxf`);
}

function dxfRect(x, y, w, h, layer, colorDecimal) {
    let str = "0\nLWPOLYLINE\n"; str += `8\n${layer}\n`; 
    if (colorDecimal) str += `420\n${colorDecimal}\n`;
    str += "90\n4\n70\n1\n"; 
    str += `10\n${x}\n20\n${y}\n`; str += `10\n${x+w}\n20\n${y}\n`;
    str += `10\n${x+w}\n20\n${y+h}\n`; str += `10\n${x}\n20\n${y+h}\n`;
    return str;
}

function dxfRoundedRect(x, y, w, h, r, layer, colorDecimal) {
    const bulge = 0.41421356;
    let str = "0\nLWPOLYLINE\n"; 
    str += `8\n${layer}\n`; 
    if (colorDecimal) str += `420\n${colorDecimal}\n`;
    str += "90\n8\n70\n1\n"; 
    str += `10\n${x+r}\n20\n${y}\n`; 
    str += `10\n${x+w-r}\n20\n${y}\n42\n${bulge}\n`; 
    str += `10\n${x+w}\n20\n${y+r}\n`; 
    str += `10\n${x+w}\n20\n${y+h-r}\n42\n${bulge}\n`;
    str += `10\n${x+w-r}\n20\n${y+h}\n`; 
    str += `10\n${x+r}\n20\n${y+h}\n42\n${bulge}\n`;
    str += `10\n${x}\n20\n${y+h-r}\n`; 
    str += `10\n${x}\n20\n${y+r}\n42\n${bulge}\n`;
    return str;
}

function downloadFile(content, filename) {
    const blob = new Blob([content], {type: 'application/dxf'});
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// ==========================================
// 7. GUI & EVENTOS
// ==========================================

const gui = new GUI({ title: 'Configuración' });

const fDims = gui.addFolder('Diseño Mueble');
fDims.add(state, 'totalWidth', 300, 1200).name('Ancho Total').onChange(generateSystem);
fDims.add(state, 'depth', 200, 800).name('Profundidad').onChange(generateSystem);
fDims.add(state, 'recess', 0, 150).name('Remetimiento').onChange(generateSystem);
fDims.addColor(state, 'color').name('Color Mueble').onChange(generateSystem);

const fBase = gui.addFolder('Base y Zoclo');
fBase.add(state, 'zocloHeight', 0, 200).name('Altura Zoclo').onChange(generateSystem);

const fGaps = gui.addFolder('Distribución Interior');
fGaps.add(state, 'shelfCount', 2, 16, 1).name('No. Huecos').onChange(rebuildGapControls);
let dynamicFolder = null;

const fProd = gui.addFolder('Producción');
fProd.add(state, 'sheetMargin', 0, 100).name('Margen a Hoja').onChange(calculateNesting);
fProd.add(state, 'partGap', 0, 50).name('Separación entre Piezas').onChange(calculateNesting);
fProd.add(state, 'dxfSpacing', 0, 1220).name('Separación entre Hojas (DXF)').onChange(() => {});

document.getElementById('btn-export').addEventListener('click', state.exportDXF);

function rebuildGapControls() {
    if (dynamicFolder) dynamicFolder.destroy();
    dynamicFolder = fGaps.addFolder('Alturas Individuales');
    dynamicFolder.open();
    
    const oldGaps = [...state.gaps];
    state.gaps = [];
    
    for(let i=0; i < state.shelfCount; i++) {
        const val = oldGaps[i] !== undefined ? oldGaps[i] : 350;
        state.gaps.push(val);
        dynamicFolder.add(state.gaps, i, 100, 1220)
            .name(`Entrepaño ${i+1}`)
            .onChange(generateSystem);
    }
    generateSystem();
}

function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
}

window.addEventListener('resize', () => {
    const w3d = container3D.clientWidth; const h3d = container3D.clientHeight;
    camera.aspect = w3d / h3d; camera.updateProjectionMatrix(); renderer.setSize(w3d, h3d);
    draw2D();
});

rebuildGapControls();
animate();